import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './academic.module.css';

/**
 * Campo de texto longo com rotulo, dica e erro associados.
 *
 * O `Field` de design cobre `input`, que e uma linha so. Os campos desta fatia
 * nao sao de uma linha: conteudo dado, evolucao, exercicios e os dois
 * comentarios aceitam 5000 caracteres cada no contrato, e a lista de valores de
 * uma escala e uma por linha. Escrever paragrafo numa caixa de uma linha, sem
 * quebra e sem poder reler o que ja foi digitado, e o atrito que faz o registro
 * pedagogico virar uma frase curta — e o registro e o que a escola tem da aula.
 *
 * Nao vira primitivo de design nesta onda: promover ao diretorio compartilhado
 * e decisao de quem cuida do design system, e nao efeito colateral de uma
 * correcao de tela. Ate la ele mora aqui e serve as duas telas academicas.
 */
export function TextAreaField({
  label,
  hint,
  error,
  value,
  required = false,
  maxLength,
  rows = 4,
  onChange,
}: {
  readonly label: string;
  readonly hint?: string | undefined;
  readonly error?: string | undefined;
  readonly value: string;
  readonly required?: boolean;
  readonly maxLength?: number | undefined;
  readonly rows?: number;
  readonly onChange: (value: string) => void;
}) {
  const { t } = useTranslation();
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const hasError = typeof error === 'string' && error.length > 0;
  const describedBy = [hint ? hintId : null, hasError ? errorId : null].filter(Boolean).join(' ');

  return (
    <div className={styles.textAreaField}>
      <label className={styles.textAreaLabel} htmlFor={id}>
        {label}
        {required ? (
          <>
            <span aria-hidden="true">{' *'}</span>
            <span className="visually-hidden">{` (${t('field.required')})`}</span>
          </>
        ) : null}
      </label>
      <textarea
        id={id}
        className={styles.textArea}
        rows={rows}
        value={value}
        required={required}
        maxLength={maxLength}
        aria-invalid={hasError ? true : undefined}
        aria-describedby={describedBy.length > 0 ? describedBy : undefined}
        onChange={(event) => onChange(event.target.value)}
      />
      {hint ? (
        <p id={hintId} className={styles.textAreaHint}>
          {hint}
        </p>
      ) : null}
      {hasError ? (
        <p id={errorId} className={styles.textAreaError} role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
